import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TwoOptionAlertComponent } from '../../../shared/components/two-option-alert/two-option-alert.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Constants } from 'src/app/app.constants';
import { AuthService } from 'src/app/services';
import { VoucherService } from 'src/app/services/voucher.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { Voucher } from 'src/app/models/voucher';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';
import { HttpErrorResponse } from '@angular/common/http';
import { StripeService } from 'src/app/services/stripe.service';
import { RedeemVoucherDialogComponent } from '../redeem-voucher-dialog/redeem-voucher-dialog.component';
import { IssueVoucherDialogComponent } from '../issue-voucher-dialog/issue-voucher-dialog.component';
import { FeesDialogComponent } from '../fees-dialog/fees-dialog.component';
import { MicrositeConfigDialogComponent } from '../microsite-config-dialog/microsite-config-dialog.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-gift-vouchers',
  templateUrl: './gift-vouchers.component.html',
  styleUrls: ['./gift-vouchers.component.css'],
})
export class GiftVouchersComponent implements OnInit {
  @ViewChild('myTable') table: any;
  subtitle = '';
  loading = false;
  generating = false;
  vouchers: any[] = [];
  length = 0;
  pageSize = 10;
  pageIndex = 0;
  canManageVouchers = false;
  saving = false;
  isConnected = false;
  isComplete = false;
  suppressPaging: boolean = false;
  hasVouchers = false;
  scrollTimeout: any;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(['(max-width: 950px)'])
    .pipe(map((result) => result.matches));

  constructor(
    private mixpanel: AnalyticsAbstract,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public constants: Constants,
    private voucherService: VoucherService,
    private ps: PermissionsService,
    private stripeService: StripeService,
    private authService: AuthService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.canManageVouchers = this.ps.adminOrMerchant;

    this.hasVouchers =
      this.authService.getNestedUserProperty('merchant', 'vouchers') ?? false;
  }

  ngOnInit() {
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');

    this.route.queryParams.subscribe((params) => {
      const setup = params['setup'];

      if (setup) {
        this.verifyStripeStatus(true).then(() => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        });
      } else {
        this.verifyStripeStatus();
      }
    });
  }

  private async verifyStripeStatus(fromSetup = false): Promise<void> {
    this.loading = true;

    this.stripeService
      .verifyConnectStatus()
      .subscribe({
        next: (res: any) => {
          this.isConnected = res.isConnected;
          this.isComplete = res.accountStatus === 'complete';

          if (res.isConnected) {
            if (fromSetup) {
              this.constants.snack('Stripe account setup successfully');
            }
          }

          this.loadVouchers();
        },
        error: (error: any) => {
          this.constants.snack('Error verifying Stripe account status');
        },
      })
      .add(() => (this.loading = false));
  }

  loadVouchers() {
    this.loading = true;

    this.voucherService
      .getVouchers(this.pageSize, this.pageIndex)
      .subscribe({
        next: (res: any) => {
          this.vouchers = res.docs;
          this.length = res.totalDocs;
        },
        error: (error: any) => {
          this.constants.snack(error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  pageEvent(event: any) {
    if (!this.suppressPaging) {
      this.pageIndex = event.offset;
      this.loadVouchers();
    }
  }

  handleScroll() {
    this.suppressPaging = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.suppressPaging = false;
    }, 100);
  }

  rowTappedNGX(event: any) {
    if (event.type === 'click') {
      this.editVoucher(event.row);
    }
  }

  createVoucher() {
    if (!this.isConnected) {
      const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
        data: {
          title: 'Stripe Setup Required',
          body: 'To create vouchers, you need to set up your Stripe account first. This allows you to receive payments securely.',
          buttonOne: 'Cancel',
          buttonTwo: 'Set Up Stripe',
        },
        scrollStrategy: new NoopScrollStrategy(),
        autoFocus: false,
        width: '350px',
        disableClose: true,
        panelClass: 'custom-dialog',
      });

      dialogRef.afterClosed().subscribe((option) => {
        if (option === 1) {
          this.setupStripe();
        }
      });
      return;
    }

    this.router.navigate([Constants.routes.giftVoucher]);
  }

  setupStripe() {
    this.generating = true;
    this.stripeService
      .getConnectLink()
      .subscribe({
        next: (res: any) => {
          if (res?.url) {
            window.location.href = res.url;
          }
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(error.error.message);
        },
      })
      .add(() => (this.generating = false));
  }

  editVoucher(voucher: any) {
    this.router.navigate([Constants.routes.giftVoucher, voucher.nanoid]);
  }

  handleMenuOptionSelection(option: string, voucher: Voucher): void {
    const dialogData = this.getDialogData(option);

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: dialogData,
      autoFocus: false,
      maxWidth: '500px',
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((selectedOption: number) => {
      if (selectedOption == 1) {
        this.toggle(option === this.constants.strings.list, voucher);
      }
    });
  }

  getDialogData(action: string) {
    const isDeactivating = action === this.constants.strings.unlist;
    return {
      title: 'Confirm',
      body: isDeactivating
        ? 'Unlisting this voucher will prevent new customers from acquiring it from the app, while current customers can still redeem it and you can still give it out manually.'
        : 'Listing this voucher will allow new customers to purchase it from the app.',
      buttonOne: 'Cancel',
      buttonTwo: action,
    };
  }

  toggle(activate: boolean, voucher: Voucher) {
    // Check minimum value for listing
    if (activate && voucher.cost < 10) {
      this.constants.snack(
        'Vouchers must have a minimum cost of £10 to be listed',
      );
      return;
    }

    this.mixpanel.track(Constants.analytics_keys.toggleVoucher, {
      card: voucher.title,
      state: activate,
    });

    this.saving = true;
    this.voucherService
      .toggleVoucher({ voucherId: voucher._id, status: activate })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.vouchers.find((el) => el._id == voucher._id)!.active =
              activate;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  get activateTitle() {
    return { title: this.constants.strings.list, icon: 'check_circle' };
  }

  get deactivateTitle() {
    return { title: this.constants.strings.unlist, icon: 'cancel' };
  }

  openStripeDashboard() {
    this.stripeService.getLoginLink().subscribe({
      next: (res: any) => {
        if (res?.url) {
          window.open(res.url, '_blank');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.constants.snack(error.error.message);
      },
    });
  }

  openRedeemVoucherDialog(): void {
    const dialogRef = this.dialog.open(RedeemVoucherDialogComponent, {
      width: '700px',
      autoFocus: false,
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadVouchers();
      }
    });
  }

  openIssueVoucherDialog(): void {
    const dialogRef = this.dialog.open(IssueVoucherDialogComponent, {
      width: '600px',
      autoFocus: false,
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadVouchers();
      }
    });
  }

  openFeesDialog(): void {
    this.dialog.open(FeesDialogComponent, {
      width: '800px',
      maxHeight: '80vh',
      autoFocus: false,
      disableClose: false,
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  openMicrositeDialog(): void {
    const dialogRef = this.dialog.open(MicrositeConfigDialogComponent, {
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh merchant data if configuration was saved
        // this.authService.refreshUser();
        window.location.reload();
      }
    });
  }

  moveVoucher(
    voucher: any,
    currentIndex: number,
    direction: 'up' | 'down',
  ): void {
    if (this.saving) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= this.vouchers.length) {
      return;
    }

    const temp = this.vouchers[currentIndex];
    this.vouchers[currentIndex] = this.vouchers[newIndex];
    this.vouchers[newIndex] = temp;

    this.vouchers.forEach((v, index) => {
      v.displayOrder = index;
    });

    this.vouchers = [...this.vouchers];

    this.saving = true;
    const updates = [
      this.voucherService.updateVoucherOrder({
        voucherId: this.vouchers[currentIndex]._id,
        displayOrder: this.vouchers[currentIndex].displayOrder,
      }),
      this.voucherService.updateVoucherOrder({
        voucherId: this.vouchers[newIndex]._id,
        displayOrder: this.vouchers[newIndex].displayOrder,
      }),
    ];

    this.saving = true;
    Promise.all(updates.map((obs) => obs.toPromise()))
      .then(() => {})
      .catch((error: any) => {
        this.constants.snack(
          error.error?.message || 'Failed to update voucher order',
        );

        this.loadVouchers();
      })
      .finally(() => (this.saving = false));
  }

  valueString(voucher: any) {
    if (voucher.voucherType === 'fixed') {
      if (voucher.value !== voucher.cost) {
        if (voucher.discount?.value > 0) {
          return `£${voucher.value} @ £${voucher.cost} (£${voucher.discount.value} off)`;
        } else {
          return `£${voucher.value} @ £${voucher.cost}`;
        }
      } else {
        return `£${voucher.value}`;
      }
    }

    return (
      '£' + voucher.monetaryOptions.min + ' - £' + voucher.monetaryOptions.max
    );
  }
}
